// src/components/common/ui.jsx
// Re-export all small UI primitives from one file
import { clsx } from 'clsx'
import { forwardRef } from 'react'
import { Loader2 } from 'lucide-react'

// ── Button ────────────────────────────────────────────────────────────────────
export function Button({ variant = 'primary', size = 'md', className, children, loading, ...props }) {
  const base   = 'inline-flex items-center gap-2 font-bold rounded-full transition-all duration-150 cursor-pointer border-0 disabled:opacity-60 disabled:cursor-not-allowed'
  const sizes  = { sm: 'px-3.5 py-1.5 text-xs', md: 'px-5 py-2.5 text-sm', lg: 'px-7 py-3 text-base' }
  const variants = {
    primary: 'bg-gradient-brand text-white shadow-brand-sm hover:shadow-brand-md hover:-translate-y-px',
    secondary: 'bg-white text-gray-800 border border-border hover:border-pink hover:text-pink',
    danger: 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100',
    ghost: 'bg-transparent text-muted hover:bg-surface hover:text-gray-900',
  }
  return (
    <button className={clsx(base, sizes[size], variants[variant], className)} disabled={loading} {...props}>
      {loading && <Loader2 size={14} className="animate-spin" />}
      {children}
    </button>
  )
}

// ── Badge ────────────────────────────────────────────────────────────────────
const badgeVariants = {
  green:  'bg-green-50 text-green-800',
  red:    'bg-red-50 text-red-700',
  yellow: 'bg-yellow-50 text-yellow-800',
  blue:   'bg-blue-50 text-blue-800',
  grey:   'bg-gray-100 text-gray-600',
  pink:   'bg-pink-light text-pink',
  purple: 'bg-purple-50 text-purple-800',
}
export function Badge({ variant = 'grey', children, className }) {
  return (
    <span className={clsx('inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold', badgeVariants[variant], className)}>
      {children}
    </span>
  )
}

// ── Status badge helper ───────────────────────────────────────────────────────
const statusMap = {
  active: 'green', approved: 'green', confirmed: 'green', delivered: 'green', published: 'green', paid: 'green',
  pending: 'yellow', processing: 'yellow', open: 'yellow', investigating: 'yellow', draft: 'yellow',
  shipped: 'blue', awaiting_payment: 'blue', trial: 'blue',
  cancelled: 'red', rejected: 'red', suspended: 'red', fraud: 'red', expired: 'grey', closed: 'grey',
}
export function StatusBadge({ status }) {
  const variant = statusMap[status] || 'grey'
  return <Badge variant={variant}>{status?.replace(/_/g, ' ')}</Badge>
}

// ── Card ─────────────────────────────────────────────────────────────────────
export function Card({ className, children, ...props }) {
  return <div className={clsx('bg-white border border-border rounded-2xl', className)} {...props}>{children}</div>
}
export function CardHeader({ className, children }) {
  return <div className={clsx('px-5 py-4 border-b border-border flex items-center justify-between flex-wrap gap-3', className)}>{children}</div>
}
export function CardBody({ className, children }) {
  return <div className={clsx('p-5', className)}>{children}</div>
}

// ── Spinner ──────────────────────────────────────────────────────────────────
export function Spinner({ size = 24 }) {
  return <Loader2 size={size} className="animate-spin text-pink" />
}
export function PageSpinner() {
  return <div className="flex items-center justify-center py-24"><Spinner size={32} /></div>
}

// ── Empty state ──────────────────────────────────────────────────────────────
export function Empty({ icon, title, description, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {icon && <div className="text-5xl mb-4">{icon}</div>}
      <h3 className="text-base font-bold text-gray-900 mb-1">{title}</h3>
      {description && <p className="text-sm text-muted max-w-xs">{description}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  )
}

// ── Table ─────────────────────────────────────────────────────────────────────
export function Table({ columns, data, loading, emptyText = 'No records found' }) {
  if (loading) return <PageSpinner />
  if (!data?.length) return <Empty icon="📭" title={emptyText} />
  return (
    <div className="overflow-x-auto">
      <table className="table-base w-full">
        <thead>
          <tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {data.map((row, i) => (
            <tr key={row.id || i}>
              {columns.map(c => <td key={c.key}>{c.render ? c.render(row) : row[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Input ─────────────────────────────────────────────────────────────────────
export const Input = forwardRef(function Input({ label, error, className, ...props }, ref) {
  return (
    <div>
      {label && <label className="block text-sm font-semibold mb-1.5">{label}</label>}
      <input ref={ref} className={clsx('input', error && 'border-red-400 focus:border-red-500', className)} {...props} />
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  )
})

// ── Select ────────────────────────────────────────────────────────────────────
export const Select = forwardRef(function Select({ label, error, options = [], className, ...props }, ref) {
  return (
    <div>
      {label && <label className="block text-sm font-semibold mb-1.5">{label}</label>}
      <select ref={ref} className={clsx('input', error && 'border-red-400', className)} {...props}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
    </div>
  )
})

// ── Pagination ────────────────────────────────────────────────────────────────
export function Pagination({ meta, onPageChange }) {
  if (!meta || meta.last_page <= 1) return null
  return (
    <div className="flex items-center justify-between px-5 py-4 border-t border-border">
      <span className="text-xs text-muted">
        Showing {meta.from}–{meta.to} of {meta.total}
      </span>
      <div className="flex gap-1">
        {Array.from({ length: meta.last_page }, (_, i) => i + 1).slice(
          Math.max(0, meta.current_page - 3),
          Math.min(meta.last_page, meta.current_page + 2)
        ).map(page => (
          <button key={page} onClick={() => onPageChange(page)}
            className={clsx('w-8 h-8 rounded-lg text-xs font-bold',
              page === meta.current_page ? 'bg-gradient-brand text-white' : 'bg-surface text-muted hover:bg-pink-light hover:text-pink'
            )}>
            {page}
          </button>
        ))}
      </div>
    </div>
  )
}

// ── Stats card ────────────────────────────────────────────────────────────────
export function StatCard({ label, value, icon, trend, color = 'pink' }) {
  const Icon = icon
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs text-muted font-semibold uppercase tracking-wide mb-1">{label}</p>
          <p className="text-2xl font-extrabold font-display">{value}</p>
          {trend && <p className={clsx('text-xs mt-1', trend > 0 ? 'text-green-600' : 'text-red-500')}>
            {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% vs last month
          </p>}
        </div>
        {Icon && (
          <div className={`w-11 h-11 rounded-2xl bg-gradient-brand flex items-center justify-center`}>
            <Icon size={20} className="text-white" />
          </div>
        )}
      </div>
    </Card>
  )
}
